import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Upload as UploadIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../../services/baseUrl";
import {
  normalizarNomeEmpresa,
  normalizarEmail,
  normalizarTelefone,
  normalizarDescricao,
  validarNomeEmpresa,
  aplicarMascaraCpfCnpj,
} from "../../utils/normalization";
import {
  getAllEstados,
  getCidadesByEstadoId,
} from "../../services/endereco/enderecoService";
import { Estado, Cidade } from "../../types/endereco/endereco";

interface Empresa {
  nome: string;
  cnpj: string;
  email: string;
  telefone: string;
  endereco: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  imagem: string;
  ativo: string;
  senha: string;
}

const CadastrarEmpresa: React.FC = () => {
  const navigate = useNavigate();

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: 2,
      "& fieldset": {
        borderColor: "#e0e0e0",
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: "#bdbdbd",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1976d2",
        borderWidth: 2,
      },
    },
    "& .MuiInputLabel-root": {
      color: "#666",
      fontSize: "0.875rem",
      "&.Mui-focused": {
        color: "#1976d2",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "16px 14px",
      color: "#333",
      "&::placeholder": {
        color: "#999",
        opacity: 1,
      },
    },
  };

  const [empresa, setEmpresa] = useState<Empresa>({
    nome: "",
    cnpj: "",
    email: "",
    telefone: "",
    endereco: "",
    cidade: "",
    estado: "",
    cep: "",
    imagem: "",
    ativo: "S",
    senha: "",
  });

  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  const [estados, setEstados] = useState<Estado[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [selectedEstadoId, setSelectedEstadoId] = useState<number | "">("");
  const [selectedCidadeId, setSelectedCidadeId] = useState<number | "">("");
  const [loadingEstados, setLoadingEstados] = useState(false);
  const [loadingCidades, setLoadingCidades] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error" | "";
    text: string;
  }>({
    type: "",
    text: "",
  });

  // Carregar estados ao montar o componente
  useEffect(() => {
    const carregarEstados = async () => {
      setLoadingEstados(true);
      try {
        const estadosData = await getAllEstados();
        setEstados(estadosData);

        // Pré-selecionar RS (Rio Grande do Sul)
        const rs = estadosData.find((e: Estado) => e.sigla === "RS");
        if (rs) {
          setSelectedEstadoId(rs.id);
          setEmpresa((prev) => ({ ...prev, estado: rs.sigla }));
        }
      } catch (error) {
        setMessage({
          type: "error",
          text: "Erro ao carregar lista de estados.",
        });
      } finally {
        setLoadingEstados(false);
      }
    };
    carregarEstados();
  }, []);

  // Carregar cidades quando um estado for selecionado
  useEffect(() => {
    const carregarCidades = async () => {
      if (selectedEstadoId !== "") {
        setLoadingCidades(true);
        try {
          const cidadesData = await getCidadesByEstadoId(
            selectedEstadoId as number,
          );
          setCidades(cidadesData);

          // Se for RS, sempre pré-selecionar Bom Retiro do Sul
          const estadoSelecionado = estados.find(
            (e) => e.id === selectedEstadoId,
          );
          if (estadoSelecionado?.sigla === "RS") {
            // Buscar por várias variações do nome de Bom Retiro do Sul
            const bomRetiro = cidadesData.find((c: Cidade) => {
              const nomeNormalizado = c.nome.toLowerCase().trim();
              return (
                nomeNormalizado === "bom retiro do sul" ||
                nomeNormalizado === "bom retiro" ||
                nomeNormalizado.includes("bom retiro do sul")
              );
            });

            if (bomRetiro) {
              setSelectedCidadeId(bomRetiro.id);
              setEmpresa((prev) => ({ ...prev, cidade: bomRetiro.nome }));
            } else {
              console.log("Bom Retiro do Sul não encontrado nas cidades do RS");
            }
          }
        } catch (error) {
          setMessage({
            type: "error",
            text: "Erro ao carregar lista de cidades.",
          });
        } finally {
          setLoadingCidades(false);
        }
      } else {
        setCidades([]);
        setSelectedCidadeId("");
        setEmpresa((prev) => ({ ...prev, cidade: "" }));
      }
    };
    carregarCidades();
  }, [selectedEstadoId, estados]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEmpresa((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = aplicarMascaraCpfCnpj(e.target.value);
    setEmpresa((prev) => ({
      ...prev,
      cnpj: formatted,
    }));
  };

  const formatarCEP = (value: string): string => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, "");
    
    // Aplica a máscara XXXXX-XXX
    if (numbers.length <= 5) {
      return numbers;
    } else {
      return numbers.slice(0, 8).replace(/(\d{5})(\d{0,3})/, "$1-$2");
    }
  };

  const handleCEPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatarCEP(e.target.value);
    setEmpresa((prev) => ({
      ...prev,
      cep: formatted,
    }));
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return numbers.replace(/^(\d{2})(\d{0,5})$/, "($1) $2");
    } else if (numbers.length <= 11) {
      return numbers.replace(/^(\d{2})(\d{5})(\d{0,4})$/, "($1) $2-$3");
    }
    
    return numbers
      .slice(0, 11)
      .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setEmpresa((prev) => ({
      ...prev,
      telefone: formatted,
    }));
  };

  const handleEstadoChange = (event: SelectChangeEvent<number | "">) => {
    const estadoId = event.target.value as number;
    setSelectedEstadoId(estadoId);

    const estado = estados.find((e) => e.id === estadoId);
    if (estado) {
      setEmpresa((prev) => ({ ...prev, estado: estado.sigla }));
    }

    // Limpar cidade quando estado mudar
    setSelectedCidadeId("");
    setEmpresa((prev) => ({ ...prev, cidade: "" }));
  };

  const handleCidadeChange = (event: SelectChangeEvent<number | "">) => {
    const cidadeId = event.target.value as number;
    setSelectedCidadeId(cidadeId);

    const cidade = cidades.find((c) => c.id === cidadeId);
    if (cidade) {
      setEmpresa((prev) => ({ ...prev, cidade: cidade.nome }));
    }
  };

  const otimizarImagem = (file: File): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = async () => {
        const maxWidth = 212; // 250 - 15% = 212
        const maxHeight = 212; // Quadrado para ficar melhor no círculo

        let outputFormat = "image/jpeg";
        let outputQuality = 0.85;

        canvas.width = maxWidth;
        canvas.height = maxHeight;

        if (ctx) {
          // Preencher fundo branco para evitar transparências
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, maxWidth, maxHeight);

          const imgAspect = img.width / img.height;
          const containerAspect = maxWidth / maxHeight; // 212/212 = 1.0 (quadrado)

          const isVeryNarrow = imgAspect < 0.8; // Imagem muito mais alta que larga
          const isVeryWide = imgAspect > 2.5; // Imagem muito mais larga que alta

          if (isVeryNarrow || isVeryWide) {
            // Para imagens extremas: usar estratégia "cover" - preencher container completamente
            let sourceX = 0,
              sourceY = 0,
              sourceWidth = img.width,
              sourceHeight = img.height;

            if (imgAspect > containerAspect) {
              // Imagem mais larga - cortar laterais para caber na altura
              sourceWidth = img.height * containerAspect;
              sourceX = (img.width - sourceWidth) / 2;
            } else {
              // Imagem mais alta/estreita - cortar topo/base para caber na largura
              sourceHeight = img.width / containerAspect;
              sourceY = (img.height - sourceHeight) / 2;
            }

            ctx.drawImage(
              img,
              sourceX,
              sourceY,
              sourceWidth,
              sourceHeight,
              0,
              0,
              maxWidth,
              maxHeight,
            );
          } else {
            // Para imagens normais: usar estratégia "contain" - preservar tudo
            let drawWidth, drawHeight, offsetX, offsetY;

            if (imgAspect > containerAspect) {
              drawWidth = maxWidth;
              drawHeight = maxWidth / imgAspect;
              offsetX = 0;
              offsetY = (maxHeight - drawHeight) / 2;
            } else {
              drawHeight = maxHeight;
              drawWidth = maxHeight * imgAspect;
              offsetX = (maxWidth - drawWidth) / 2;
              offsetY = 0;
            }

            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          }
          const dataUrl = canvas.toDataURL(outputFormat, outputQuality);
          resolve(dataUrl);
        } else {
          reject(new Error("Erro ao processar imagem"));
        }
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/jpg,image/png,image/gif,image/webp";
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (file) {
        // Validar tipo de arquivo
        const tiposPermitidos = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        if (!tiposPermitidos.includes(file.type)) {
          setMessage({
            type: "error",
            text: "Apenas arquivos JPEG, PNG, GIF e WebP são permitidos.",
          });
          return;
        }

        // Validar tamanho (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
          setMessage({ type: "error", text: "A imagem deve ter no máximo 5MB." });
          return;
        }

        try {
          setMessage({ type: "", text: "Redimensionando imagem..." });

          const imagemOtimizada = await otimizarImagem(file);

          setEmpresa((prev) => ({
            ...prev,
            imagem: imagemOtimizada,
          }));
          setMessage({
            type: "success",
            text: "Imagem otimizada com sucesso! Redimensionamento inteligente aplicado.",
          });
        } catch (error) {
          console.error("❌ Erro ao otimizar imagem:", error);
          setMessage({
            type: "error",
            text: "Erro ao processar imagem. Tente novamente.",
          });
        }
      }
    };
    input.click();
  };

  const clearForm = () => {
    setEmpresa({
      nome: "",
      cnpj: "",
      email: "",
      telefone: "",
      endereco: "",
      cidade: "",
      estado: "",
      cep: "",
      imagem: "",
      ativo: "S",
      senha: "",
    });
    setConfirmarSenha("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação dos campos obrigatórios
    if (
      !empresa.nome ||
      !empresa.cnpj ||
      !empresa.email ||
      !empresa.senha ||
      !empresa.cidade ||
      !empresa.estado
    ) {
      setMessage({
        type: "error",
        text: "Por favor, preencha todos os campos obrigatórios (Nome, CPF/CNPJ, E-mail, Cidade, Estado e Senha).",
      });
      return;
    }

    // Validar nome da empresa - aceita números e caracteres especiais comuns
    if (!validarNomeEmpresa(empresa.nome)) {
      setMessage({
        type: "error",
        text: "Nome da empresa inválido! Use apenas letras, números e caracteres válidos (vírgula, ponto, parênteses, etc.).",
      });
      return;
    }

    // Validação do formato do CPF/CNPJ
    const cnpjLimpo = empresa.cnpj.replace(/\D/g, "");
    if (cnpjLimpo.length !== 11 && cnpjLimpo.length !== 14) {
      setMessage({
        type: "error",
        text: "CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos.",
      });
      return;
    }

    // Validação da senha
    if (empresa.senha.length < 6) {
      setMessage({
        type: "error",
        text: "A senha deve ter pelo menos 6 caracteres.",
      });
      return;
    }

    // Validação da confirmação de senha
    if (empresa.senha !== confirmarSenha) {
      setMessage({ type: "error", text: "As senhas não coincidem." });
      return;
    }

    // Validação da cidade - apenas Bom Retiro do Sul/RS é permitida
    if (empresa.cidade && empresa.estado) {
      const cidadeNormalizada = empresa.cidade.toLowerCase().trim();
      const estadoNormalizado = empresa.estado.toUpperCase().trim();

      const isBomRetiro =
        cidadeNormalizada === "bom retiro do sul" ||
        cidadeNormalizada.includes("bom retiro");
      const isRS = estadoNormalizado === "RS";

      if (!isBomRetiro || !isRS) {
        setMessage({
          type: "error",
          text: `Empresa localizada em ${empresa.cidade}/${empresa.estado}. Aceitas apenas empresas de Bom Retiro do Sul/RS.`,
        });
        return;
      }
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      // Normalizar e remover formatação antes de enviar
      const empresaParaEnvio = {
        ...empresa,
        nome: normalizarNomeEmpresa(empresa.nome),
        email: normalizarEmail(empresa.email),
        telefone: normalizarTelefone(empresa.telefone),
        endereco: normalizarDescricao(empresa.endereco),
        cep: empresa.cep || "", // Mantém formatação do CEP (XXXXX-XXX)
        cnpj: empresa.cnpj.replace(/\D/g, ""), // Remove pontos, barras e traços
        imagem: empresa.imagem || "", // Enviar a imagem base64 se houver
      };

      const apiUrl = `${getApiBaseUrl()}/empresas`;

      const response = await axios.post(apiUrl, empresaParaEnvio, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
      });

      if (response.data.success && response.data.data) {
        const empresaCriada = response.data.data;

        if (empresaCriada.id && empresa.imagem) {
          localStorage.setItem(
            `empresaImagem_${empresaCriada.id}`,
            empresa.imagem,
          );
        }

        // Fazer login automático com as credenciais usadas no cadastro
        try {
          const loginData = {
            cnpj: empresa.cnpj,
            senha: empresa.senha, // Usar a senha original antes de limpar o form
          };

          const loginUrl = `${getApiBaseUrl()}/empresas/login`;
          const loginResponse = await axios.post(loginUrl, loginData, {
            headers: {
              "Content-Type": "application/json",
            },
          });

          // A resposta vem diretamente em loginResponse.data, não em data.data
          if (loginResponse.data && loginResponse.data.token) {
            const loginDataResponse = loginResponse.data;

            // Salvar dados no localStorage
            localStorage.setItem("token", loginDataResponse.token);
            localStorage.setItem("userType", "empresa");
            localStorage.setItem(
              "userId",
              loginDataResponse.empresaId?.toString() || "",
            );
            localStorage.setItem(
              "userName",
              loginDataResponse.nomeEmpresa || "",
            );

            if (loginDataResponse.cnpj) {
              localStorage.setItem("userCnpj", loginDataResponse.cnpj);
            }

            // Salvar todos os dados da empresa
            localStorage.setItem(
              "empresaData",
              JSON.stringify({
                id: loginDataResponse.empresaId,
                nome: loginDataResponse.nomeEmpresa,
                cnpj: loginDataResponse.cnpj,
                email: loginDataResponse.email,
                telefone: loginDataResponse.telefone || "",
                imagem: "",
                ativo: "S",
              }),
            );

            setEmpresa({
              nome: "",
              cnpj: "",
              email: "",
              telefone: "",
              endereco: "",
              cidade: "",
              estado: "",
              cep: "",
              imagem: "",
              ativo: "S",
              senha: "",
            });
            setConfirmarSenha("");

            setMessage({
              type: "success",
              text: "Empresa cadastrada com sucesso! Redirecionando...",
            });

            // Redirecionar para dashboard
            navigate("/dashboard-empresa");
          } else {
            // Se não conseguiu fazer login automático, redirecionar para login
            throw new Error("Token não encontrado - redirecionando para login");
          }
        } catch (loginError) {
          setMessage({
            type: "success",
            text: "Empresa cadastrada com sucesso! Redirecionando para o login...",
          });
          setTimeout(() => {
            navigate("/login-empresa");
          }, 2000);
        }
      } else {
        // Se não teve sucesso no cadastro, mostrar erro
        setMessage({
          type: "error",
          text: "Erro ao cadastrar empresa. Verifique os dados e tente novamente.",
        });
      }
    } catch (error: any) {
      let errorMessage = "Erro ao cadastrar empresa. Tente novamente.";

      // Capturar mensagem específica do backend
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 403) {
        errorMessage = "Empresa não autorizada para cadastro. Entre em contato com a prefeitura.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f5f5",
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, pt: 6, backgroundColor: "#ffffff" }}>
          <Box display="flex" alignItems="center" mb={3}>
            <BusinessIcon sx={{ mr: 2, fontSize: 40, color: "primary.main" }} />
            <Typography variant="h4" component="h1" sx={{ color: "#333" }}>
              Cadastre sua Empresa
            </Typography>
          </Box>

          {message.text && (
            <Alert
              severity={message.type as any}
              sx={{ mb: 3 }}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <Stack spacing={3}>
              {/* CNPJ */}
              <TextField
                fullWidth
                label="CPF/CNPJ da Empresa"
                name="cnpj"
                value={empresa.cnpj}
                onChange={handleCNPJChange}
                required
                variant="outlined"
                autoComplete="off"
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                inputProps={{ maxLength: 18 }}
                sx={textFieldStyles}
                helperText="Digite o CPF (pessoa física) ou CNPJ (pessoa jurídica)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ color: "#666", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Nome */}
              <TextField
                fullWidth
                label="Nome da Empresa"
                name="nome"
                value={empresa.nome}
                onChange={handleChange}
                required
                variant="outlined"
                autoComplete="off"
                inputProps={{ maxLength: 255 }}
                sx={textFieldStyles}
                helperText="Para MEI: XX.XXX.XXX + nome do(a) empreendedor(a)"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ color: "#666", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Email e Telefone */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <TextField
                  fullWidth
                  label="E-mail"
                  name="email"
                  type="email"
                  value={empresa.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  autoComplete="off"
                  inputProps={{ maxLength: 255 }}
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#666", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Telefone"
                  name="telefone"
                  value={empresa.telefone}
                  onChange={handlePhoneChange}
                  variant="outlined"
                  autoComplete="off"
                  placeholder="(00) 00000-0000"
                  inputProps={{ maxLength: 15 }}
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: "#666", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Endereço */}
              <TextField
                fullWidth
                label="Endereço"
                name="endereco"
                value={empresa.endereco}
                onChange={handleChange}
                variant="outlined"
                autoComplete="off"
                inputProps={{ maxLength: 255 }}
                sx={textFieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeIcon sx={{ color: "#666", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Estado, Cidade e CEP */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <FormControl
                  sx={{
                    ...textFieldStyles,
                    width: { xs: "100%", md: "200px" },
                  }}
                  required
                >
                  <InputLabel id="estado-label">Estado</InputLabel>
                  <Select
                    labelId="estado-label"
                    id="estado-select"
                    value={selectedEstadoId}
                    label="Estado"
                    onChange={handleEstadoChange}
                    disabled={loadingEstados}
                  >
                    {estados.map((estado) => (
                      <MenuItem key={estado.id} value={estado.id}>
                        {estado.sigla} - {estado.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  fullWidth
                  sx={textFieldStyles}
                  required
                  disabled={!selectedEstadoId || loadingCidades}
                >
                  <InputLabel id="cidade-label">Cidade</InputLabel>
                  <Select
                    labelId="cidade-label"
                    id="cidade-select"
                    value={selectedCidadeId}
                    label="Cidade"
                    onChange={handleCidadeChange}
                  >
                    {cidades.map((cidade) => (
                      <MenuItem key={cidade.id} value={cidade.id}>
                        {cidade.nome}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedEstadoId && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 0.5, ml: 1.5, color: "#666" }}
                    >
                      Apenas empresas de Bom Retiro do Sul/RS são aceitas
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="CEP"
                  name="cep"
                  value={empresa.cep}
                  onChange={handleCEPChange}
                  variant="outlined"
                  autoComplete="off"
                  placeholder="00000-000"
                  inputProps={{ maxLength: 9 }}
                  sx={{
                    ...textFieldStyles,
                    width: { xs: "100%", md: "150px" },
                  }}
                />
              </Box>

              {/* Senhas */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <TextField
                  fullWidth
                  label="Senha"
                  name="senha"
                  type={mostrarSenha ? "text" : "password"}
                  value={empresa.senha}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  autoComplete="new-password"
                  inputProps={{ minLength: 6 }}
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#666", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setMostrarSenha(!mostrarSenha)}
                          edge="end"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              color: "#1976d2",
                              backgroundColor: "rgba(25, 118, 210, 0.04)",
                            },
                          }}
                        >
                          {mostrarSenha ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Mínimo 6 caracteres"
                />
                <TextField
                  fullWidth
                  label="Confirmar Senha"
                  name="confirmarSenha"
                  type={mostrarConfirmarSenha ? "text" : "password"}
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  required
                  variant="outlined"
                  autoComplete="new-password"
                  sx={textFieldStyles}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#666", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setMostrarConfirmarSenha(!mostrarConfirmarSenha)
                          }
                          edge="end"
                          sx={{
                            color: "#666",
                            "&:hover": {
                              color: "#1976d2",
                              backgroundColor: "rgba(25, 118, 210, 0.04)",
                            },
                          }}
                        >
                          {mostrarConfirmarSenha ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={
                    confirmarSenha !== "" && empresa.senha !== confirmarSenha
                  }
                  helperText={
                    confirmarSenha !== "" && empresa.senha !== confirmarSenha
                      ? "As senhas não coincidem"
                      : ""
                  }
                />
              </Box>

              {/* Imagem */}
              <Box>
                <TextField
                  fullWidth
                  label="Imagem/Logo (Opcional)"
                  name="imagem"
                  value={empresa.imagem ? "Imagem selecionada" : ""}
                  variant="outlined"
                  autoComplete="off"
                  placeholder="Clique no ícone para selecionar uma imagem"
                  sx={textFieldStyles}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <UploadIcon sx={{ color: "#666", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleImageUpload}
                          edge="end"
                          sx={{ color: "#666" }}
                        >
                          <UploadIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Preview da imagem */}
                {empresa.imagem && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 212,
                        height: 212,
                        borderRadius: "50%", // Circular
                        overflow: "hidden",
                        border: "2px solid #1976d2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      <img
                        src={empresa.imagem}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="success.main">
                        ✓ Imagem otimizada (212x212)
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5, color: "#666" }}>
                        Redimensionamento inteligente aplicado
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          setEmpresa((prev) => ({ ...prev, imagem: "" }))
                        }
                        sx={{ mt: 1 }}
                      >
                        Remover
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Botões */}
              <Box display="flex" justifyContent="center" gap={2} mt={4}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/login-empresa")}
                  disabled={loading}
                  startIcon={<ArrowBackIcon />}
                  sx={{ minWidth: 150 }}
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <BusinessIcon />
                    )
                  }
                  sx={{
                    minWidth: 200,
                    backgroundColor: "#4caf50",
                    "&:hover": {
                      backgroundColor: "#45a049",
                    },
                  }}
                >
                  {loading ? "Cadastrando..." : "Cadastrar Empresa"}
                </Button>
              </Box>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default CadastrarEmpresa;
